import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    forwardRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';

import { BooleanInput } from '@angular/cdk/coercion';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseVerticalNavigationBasicItemComponent } from '@fuse/components/navigation/vertical/components/basic/basic.component';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseVerticalNavigationDividerItemComponent } from '@fuse/components/navigation/vertical/components/divider/divider.component';
import { FuseVerticalNavigationGroupItemComponent } from '@fuse/components/navigation/vertical/components/group/group.component';
import { FuseVerticalNavigationSpacerItemComponent } from '@fuse/components/navigation/vertical/components/spacer/spacer.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@ngneat/transloco';
import { fuseAnimations } from '@fuse/animations';

@Component({
    selector: 'fuse-vertical-navigation-collapsable-item',
    templateUrl: './collapsable.component.html',
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgClass,
        MatTooltipModule,
        NgIf,
        MatIconModule,
        NgFor,
        FuseVerticalNavigationBasicItemComponent,
        forwardRef(() => FuseVerticalNavigationCollapsableItemComponent),
        FuseVerticalNavigationDividerItemComponent,
        FuseVerticalNavigationGroupItemComponent,
        FuseVerticalNavigationSpacerItemComponent,
        TranslocoModule,
    ],
})
export class FuseVerticalNavigationCollapsableItemComponent
    implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_autoCollapse: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() autoCollapse: boolean;
    @Input() item: FuseNavigationItem;
    @Input() name: string;

    isCollapsed: boolean = true;
    isExpanded: boolean = false;
    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _fuseNavigationService: FuseNavigationService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            'fuse-vertical-navigation-item-collapsed': this.isCollapsed,
            'fuse-vertical-navigation-item-expanded': this.isExpanded,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the parent navigation component
        this._fuseVerticalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // Vérifie que le composant existe
        if (!this._fuseVerticalNavigationComponent) {
            console.warn(
                `[FuseVerticalNavigation] Aucun composant trouvé avec le nom '${this.name}'`
            );
            return;
        }

        // Expand si un enfant est actif, sinon collapse si autoCollapse
        if (this._hasActiveChild(this.item, this._router.url)) {
            this.expand();
        } else if (this.autoCollapse) {
            this.collapse();
        }

        // Sécurise les abonnements : onCollapsableItemCollapsed
        if (
            this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed
                ?.pipe
        ) {
            this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((collapsedItem) => {
                    if (
                        collapsedItem &&
                        this._isChildrenOf(collapsedItem, this.item)
                    ) {
                        this.collapse();
                    }
                });
        }

        // Sécurise les abonnements : onCollapsableItemExpanded
        if (
            this.autoCollapse &&
            this._fuseVerticalNavigationComponent.onCollapsableItemExpanded
                ?.pipe
        ) {
            this._fuseVerticalNavigationComponent.onCollapsableItemExpanded
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((expandedItem) => {
                    if (
                        expandedItem &&
                        !this._isChildrenOf(this.item, expandedItem) &&
                        !this._hasActiveChild(this.item, this._router.url) &&
                        this.item !== expandedItem
                    ) {
                        this.collapse();
                    }
                });
        }

        // Écoute les changements de route
        this._router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                if (this._hasActiveChild(this.item, event.urlAfterRedirects)) {
                    this.expand();
                } else if (this.autoCollapse) {
                    this.collapse();
                }
            });

        // Écoute le rafraîchissement du composant navigation (optionnel mais utile)
        if (this._fuseVerticalNavigationComponent.onRefreshed?.pipe) {
            this._fuseVerticalNavigationComponent.onRefreshed
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this._changeDetectorRef.markForCheck();
                });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Collapse
     */
    collapse(): void {
        if (this.item.disabled || this.isCollapsed) return;

        this.isCollapsed = true;
        this.isExpanded = false;

        this._changeDetectorRef.markForCheck();

        // ✅ Ajoute une vérif complète
        if (
            this._fuseVerticalNavigationComponent &&
            this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed &&
            typeof this._fuseVerticalNavigationComponent
                .onCollapsableItemCollapsed.next === 'function'
        ) {
            this._fuseVerticalNavigationComponent.onCollapsableItemCollapsed.next(
                this.item
            );
        } else {
            console.warn(
                '[FuseVerticalNavigationCollapsableItem] onCollapsableItemCollapsed is undefined for',
                this.name
            );
        }
    }

    /**
     * Expand
     */
    expand(): void {
        if (this.item.disabled || !this.isCollapsed) return;

        this.isCollapsed = false;
        this.isExpanded = true;

        this._changeDetectorRef.markForCheck();

        if (
            this._fuseVerticalNavigationComponent &&
            this._fuseVerticalNavigationComponent.onCollapsableItemExpanded &&
            typeof this._fuseVerticalNavigationComponent
                .onCollapsableItemExpanded.next === 'function'
        ) {
            this._fuseVerticalNavigationComponent.onCollapsableItemExpanded.next(
                this.item
            );
        } else {
            console.warn(
                '[FuseVerticalNavigationCollapsableItem] onCollapsableItemExpanded is undefined for',
                this.name
            );
        }
    }

    /**
     * Toggle collapsable
     */
    toggleCollapsable(): void {
        // Toggle collapse/expand
        if (this.isCollapsed) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check if the given item has the given url
     * in one of its children
     *
     * @param item
     * @param currentUrl
     * @private
     */
    private _hasActiveChild(
        item: FuseNavigationItem,
        currentUrl: string
    ): boolean {
        const children = item.children;

        if (!children) {
            return false;
        }

        for (const child of children) {
            if (child.children) {
                if (this._hasActiveChild(child, currentUrl)) {
                    return true;
                }
            }

            // Check if the child has a link and is active
            if (
                child.link &&
                this._router.isActive(child.link, child.exactMatch || false)
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if this is a children
     * of the given item
     *
     * @param parent
     * @param item
     * @private
     */
    private _isChildrenOf(
        parent: FuseNavigationItem,
        item: FuseNavigationItem
    ): boolean {
        const children = parent.children;

        if (!children) {
            return false;
        }

        if (children.indexOf(item) > -1) {
            return true;
        }

        for (const child of children) {
            if (child.children) {
                if (this._isChildrenOf(child, item)) {
                    return true;
                }
            }
        }

        return false;
    }
}
